import React, { useMemo } from 'react';
import { FiCalendar } from 'react-icons/fi';
import PostCard from './PostCard';
import PostEmptyState from './PostEmptyStates';
import { formatPstDate, formatPstTime, getPstDayKey } from './SchedulePicker';

function groupByDay(posts) {
  const groups = {};
  const noDate = [];
  posts.forEach((post) => {
    const key = post.scheduled_at ? getPstDayKey(post.scheduled_at) : null;
    if (key) {
      if (!groups[key]) groups[key] = { key, date: post.scheduled_at, posts: [] };
      groups[key].posts.push(post);
    } else {
      noDate.push(post);
    }
  });

  const sorted = Object.values(groups).sort((a, b) => a.key.localeCompare(b.key));
  sorted.forEach((g) => {
    g.posts.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  });

  return { dayGroups: sorted, unscheduled: noDate };
}

function isToday(dayKey) {
  const today = getPstDayKey(new Date().toISOString());
  return dayKey === today;
}

function isTomorrow(dayKey) {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return dayKey === getPstDayKey(tomorrow.toISOString());
}

function getDayLabel(dayKey, dateIso) {
  if (isToday(dayKey)) return 'Today';
  if (isTomorrow(dayKey)) return 'Tomorrow';
  return formatPstDate(dateIso);
}

export default function PostTimeline({
  posts = [],
  onEdit,
  onApprove,
  onQueue,
  onCancel,
  onRetry,
  onClick,
  loading = false,
}) {
  const { dayGroups, unscheduled } = useMemo(() => groupByDay(posts), [posts]);

  if (loading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="h-5 w-32 bg-zinc-200 rounded mb-3" />
            <div className="space-y-3 ml-6">
              <div className="h-28 bg-zinc-100 rounded-2xl" />
              <div className="h-28 bg-zinc-100 rounded-2xl" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (dayGroups.length === 0 && unscheduled.length === 0) {
    return <PostEmptyState type="timeline" />;
  }

  return (
    <div className="space-y-8">
      {dayGroups.map((group) => {
        const label = getDayLabel(group.key, group.date);
        const today = isToday(group.key);

        return (
          <div key={group.key}>
            {/* Day header */}
            <div className="flex items-center gap-3 mb-4">
              <div className={`
                flex items-center justify-center w-10 h-10 rounded-xl
                ${today ? 'bg-indigo-100 text-indigo-600' : 'bg-zinc-100 text-zinc-500'}
              `}>
                <FiCalendar className="w-5 h-5" />
              </div>
              <div>
                <h3 className={`text-sm font-semibold ${today ? 'text-indigo-700' : 'text-zinc-800'}`}>
                  {label}
                </h3>
                <p className="text-xs text-zinc-400">{group.posts.length} post{group.posts.length !== 1 ? 's' : ''}</p>
              </div>
              {today && (
                <span className="ml-2 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 text-[10px] font-semibold uppercase tracking-wider">
                  Today
                </span>
              )}
            </div>

            {/* Timeline spine */}
            <div className="relative ml-5 pl-6 border-l-2 border-zinc-200 space-y-4">
              {group.posts.map((post) => (
                <div key={post._id} className="relative">
                  {/* Time marker */}
                  <div className="absolute -left-[33px] top-4 flex items-center">
                    <div className={`
                      w-3 h-3 rounded-full border-2 border-white shadow-sm
                      ${post.status === 'posted' ? 'bg-emerald-500' :
                        post.status === 'failed' ? 'bg-red-500' :
                        post.status === 'queued' ? 'bg-indigo-500' :
                        post.status === 'publishing' ? 'bg-violet-500' :
                        'bg-zinc-400'}
                    `} />
                  </div>
                  <div className="absolute -left-[90px] top-3 text-[11px] font-medium text-zinc-400 w-14 text-right">
                    {formatPstTime(post.scheduled_at)}
                  </div>
                  <PostCard
                    post={post}
                    onEdit={onEdit}
                    onApprove={onApprove}
                    onQueue={onQueue}
                    onCancel={onCancel}
                    onRetry={onRetry}
                    onClick={onClick}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Unscheduled posts */}
      {unscheduled.length > 0 && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-amber-100 text-amber-600">
              <FiCalendar className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-amber-700">Unscheduled</h3>
              <p className="text-xs text-zinc-400">{unscheduled.length} post{unscheduled.length !== 1 ? 's' : ''} without a schedule</p>
            </div>
          </div>
          <div className="ml-5 pl-6 space-y-4">
            {unscheduled.map((post) => (
              <PostCard
                key={post._id}
                post={post}
                onEdit={onEdit}
                onApprove={onApprove}
                onQueue={onQueue}
                onCancel={onCancel}
                onRetry={onRetry}
                onClick={onClick}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
